import { frameId } from "../assets/manifest.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type WorldDocument } from "../document/types.ts";
import type { ValidationIssue } from "../editor/adapter.ts";
import { frameSizeOf } from "./behaviour.ts";
import type { CompileContext } from "./context.ts";
import { FOOTPRINT_DEFAULT_CELL } from "./footprint-cell.ts";
import { standMarksIssue } from "./stand-cells.ts";
import { ownMarksOf, type StandFrame } from "./stand-marks.ts";

/**
 * Frames a placement's own marks (ADR-132 decision 12) may lie on even where the frame itself is painted with
 * nothing.
 */

/**
 * A frame lookup that also answers for a frame the context can size but paints nothing: such a frame has no marks of
 * its own, and exists so a placement's own Behind and In front cells have a frame to be laid on. Its base is the one
 * the frame draws with (`drawn`, else the context's `frameSizes`, else the frame's bottom row), never the
 * footprint's, as `framesOf` reads it; a ground frame is left out, as `framesOf` leaves it out.
 *
 * @param frameOf The lookup of painted frames (`framesOf`).
 * @param context The compile context, for the frames' sizes, draw bases and footprints.
 * @param drawn What the frame is drawn with, where the caller holds it: the editor's atlas rect.
 * @returns The lookup.
 */
export function withBareFrames(frameOf: (frame: string) => StandFrame | undefined, context: Pick<CompileContext, "collision" | "frameSizes">, drawn?: (frame: string) => { readonly base: number; readonly ground?: true } | undefined): (frame: string) => StandFrame | undefined {
  return (frame) => {
    const painted = frameOf(frame);
    if (painted !== undefined) {
      return painted;
    }
    const footprint = context.collision.footprints?.[frame];
    const size = context.frameSizes?.[frame];
    const w = footprint?.w ?? size?.w;
    const h = footprint?.h ?? size?.h;
    const draws = drawn?.(frame) ?? size;
    return w === undefined || h === undefined || draws?.ground === true ? undefined : { w, h, base: draws?.base ?? h, cell: FOOTPRINT_DEFAULT_CELL, behind: [], front: [] };
  };
}

/**
 * The frames whose draw base a compile of the world bakes stand depths at (ADR-132 decisions 5 and 12): every frame the
 * context paints, and the frame of every placed prop that holds its own Behind or In front list, an empty one included.
 * The caller gives each the base and ground flag the atlas build writes for it before the stand table is laid, so a
 * copy's own marks on a frame painted with nothing are baked at the line the renderer draws it by.
 *
 * @param world The document.
 * @param standMarks The context's painted frames.
 * @returns The frame ids.
 */
export function standFrameIds(world: WorldDocument, standMarks: CompileContext["standMarks"]): ReadonlySet<string> {
  const ids = new Set(Object.keys(standMarks ?? {}));
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const prop of layer.props) {
        if (ownMarksOf(prop.footprint) !== undefined) {
          ids.add(frameId(prop.frame.tileset, null, prop.frame.tile));
        }
      }
    }
  }
  return ids;
}

/**
 * The placed props of a map whose own Behind or In front cells (ADR-132 decision 12) lie more than `STAND_REACH` cells
 * outside their frame's grid, counted in the authoring cell they are held in. The document's own checks
 * (`instanceFootprintProblem`, run by commands, live ops and the world reader) bound the cells without the frame, whose
 * size only the compile context knows; this holds them to the reach the library's routes hold a frame's marks to, where
 * the world is validated against its context. A frame the context cannot size is left to those checks.
 *
 * @param map The map.
 * @param context The compile context, for the frames' sizes.
 * @param tileSize Tile size in pixels.
 * @returns One refusal per such prop, naming it, the cell and the frame's grid.
 */
export function ownMarksIssues(map: MapDocument, context: Pick<CompileContext, "collision" | "frameSizes">, tileSize: number): ValidationIssue[] {
  const cell = tileSize / COLLISION_CELLS_PER_TILE;
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const own = ownMarksOf(prop.footprint);
      if (own === undefined) {
        continue;
      }
      const id = frameId(prop.frame.tileset, null, prop.frame.tile);
      const size = frameSizeOf(context, id);
      if (size === undefined) {
        continue;
      }
      const reason = standMarksIssue({ ...own, cell }, { w: size.w, h: size.h });
      if (reason !== undefined) {
        issues.push({ mapId: map.id, layerId: layer.id, propId: prop.id, message: `prop ${prop.id} holds its own marks out of reach: ${reason}` });
      }
    }
  }
  return issues;
}
